<template>
  <!-- Edit Dialog -->
  <base-dialog
    v-model="dialog.show"
    max-width="700px"
    min-width="400px"
    scrollable
    @close="onDialogClose"
    :title="`${titleDialog} ${$t('sidebar.datastore.name')}`"
    width="50%"
  >
    <base-form ref="form" lazy-validation @submit.stop="onSubmit">
      <v-row no-gutters>
        <v-col cols="12">
          <field-group
            v-model="dialog.data.name"
            autofocus
            filled
            :rules="nameRules"
            label="Tên hiển thị"
            no-label
          ></field-group>
        </v-col>
        <v-col cols="12" v-if="isCreateDialog">
          <field-group
            item-text="name"
            item-value="value"
            v-model="dialog.data.type"
            filled
            :items="dataStoreTypes"
            :rules="typeRules"
            :error-messages="dialog.error.type"
            label="Loại"
            no-label
            typeInput="v-select"
          >
          </field-group>
        </v-col>
        <component
          :is="typeFormComponent"
          v-if="typeFormComponent"
          :value="dialog.data"
          :errors="dialog.error"
          :is-create="isCreateDialog"
          :srs.sync="srs"
          :srs-list="srsList"
          :loading-syn-api="loadingSynApi"
          @sync-api="updateApiData"
        />
      </v-row>
    </base-form>

    <template v-slot:extra-btn>
      <base-button
        color="primary"
        :loading="dialog.loading"
        outlined
        :type-action="dialog.type"
        @click="onSubmit"
      >
      </base-button>
    </template>
  </base-dialog>
</template>

<script>
import { EDIT_TYPE } from '@constant'
import { API_DATASTORE, API_SRS } from '@api'
import { mapGetters } from 'vuex'
import ShapefileForm from './dialog-forms/shapefile-form.vue'
import TifForm from './dialog-forms/tif-form.vue'
import PdfForm from './dialog-forms/pdf-form.vue'
import ApiForm from './dialog-forms/api-form.vue'
import { createDatastoreTypeHandler } from '@service/datastore-type'

const TYPE_FORM_MAP = {
  shapefile: 'ShapefileForm',
  tif: 'TifForm',
  pdf: 'PdfForm',
  api: 'ApiForm',
}

export default {
  components: {
    ShapefileForm,
    TifForm,
    PdfForm,
    ApiForm,
  },
  props: {
    workspace_id: {
      type: [String, Number],
      required: true,
    },
  },
  data: () => ({
    nameRules: [(v) => !!v || 'Tên không thể bỏ trống'],
    typeRules: [(v) => !!v || 'Loại không thể bỏ trống'],
    dialog: {
      show: false,
      loading: false,
      data: {},
      type: EDIT_TYPE.CREATE,
      error: {},
    },
    loadingSynApi: false,
    dataStoreTypes: [
      { name: 'Shapefile', value: 'shapefile' },
      { name: 'GeoTIFF (.tif)', value: 'tif' },
      { name: 'GeoPDF (.pdf)', value: 'pdf' },
      { name: 'Link API từ MTQG', value: 'api' },
    ],
    srsList: [],
    srs: null,
  }),
  watch: {
    'dialog.data.type'(type) {
      if (!this.isCreateDialog) return
      this.typeHandler(type).applyTypeDefaults(
        this.dialog.data,
        (key, value) => {
          this.$set(this.dialog.data, key, value)
        }
      )
    },
  },
  created() {
    this.fetchSrsList()
  },
  computed: {
    ...mapGetters({
      currentId: 'auth/currentId',
      currentUser: 'auth/currentUser',
    }),
    titleDialog() {
      if (this.isCreateDialog) {
        return this.$i18n.t('actions.add')
      } else {
        return this.$i18n.t('actions.update')
      }
    },
    isCreateDialog() {
      return this.dialog.type == EDIT_TYPE.CREATE
    },
    typeFormComponent() {
      return TYPE_FORM_MAP[this.dialog.data.type] || null
    },
  },
  methods: {
    typeHandler(type) {
      return createDatastoreTypeHandler(type || this.dialog.data.type)
    },
    open(item, type = EDIT_TYPE.CREATE) {
      if (this.$refs.form) this.$refs.form.resetValidation()
      this.dialog.show = true
      this.dialog.type = type
      this.srs = null
      const data = Object.assign({}, item)
      if (
        item &&
        item.details &&
        item.details.length > 0 &&
        item.details[0].type == 'api'
      ) {
        data.type = 'api'
        data.api = item.details[0].api_link
      }
      this.typeHandler(data.type).prepareFormData(data)
      this.$set(this.dialog, 'data', data)
    },
    onDialogClose() {
      this.dialog = {
        show: false,
        loading: false,
        data: {},
        type: EDIT_TYPE.CREATE,
        error: {},
      }
      this.srs = null
    },
    buildCreatePayload() {
      return this.typeHandler(this.dialog.data.type).buildCreatePayload(
        this.dialog.data,
        {
          workspace_id: this.workspace_id,
          srs: this.srs,
        }
      )
    },
    rasterCreatePayload() {
      return this.typeHandler(this.dialog.data.type).buildCreatePayload(
        this.dialog.data,
        { workspace_id: this.workspace_id }
      )
    },
    appendFormValue(formData, key, value) {
      if (value === undefined || value === null) return
      if (
        Array.isArray(value) ||
        (typeof FileList !== 'undefined' && value instanceof FileList)
      ) {
        Array.from(value).forEach((item) => {
          if (item != null) formData.append(key, item)
        })
        return
      }
      formData.append(key, value)
    },
    async onSubmit() {
      this.dialog.error = {}
      if (!(await this.$refs.form.validate())) return
      if (this.isCreateDialog) {
        const result = this.typeHandler(
          this.dialog.data.type
        ).validateCreate(this.dialog.data, { isCreate: true, srs: this.srs })
        if (!result.ok) {
          this.dialog.error = result.errors || {}
          return
        }
      }
      this.dialog.loading = true
      try {
        if (!this.isCreateDialog) {
          const payload = this.typeHandler(
            this.dialog.data.type
          ).buildUpdatePayload(this.dialog.data)
          const res = await API_DATASTORE.update(this.dialog.data.id, payload)
          if (res && !res.error) {
            this.onDialogClose()
            this.getData()
          }
          if (
            res &&
            res.error &&
            res.error.response &&
            res.error.response.status == 422
          ) {
            this.dialog.error = res.error.response.data.errors || {}
          }
          return
        }

        const data = this.buildCreatePayload()
        const formData = new FormData()
        Object.keys(data).forEach((key) => {
          this.appendFormValue(formData, key, data[key])
        })

        this.$store.dispatch('datastore/callUpload', {
          url: `datastores`,
          data: formData,
          cbWhenComplete: () => {
            this.getData()
          },
        })
        this.onDialogClose()
      } finally {
        this.dialog.loading = false
      }
    },
    getData() {
      this.$emit('reset')
    },
    async updateApiData(item) {
      this.loadingSynApi = true
      let res = await API_DATASTORE.sync(item.id)
      this.loadingSynApi = false
      if (res.isDone) {
        this.dialog.data.time_data = res.data.data.time_data
      }
    },
    async fetchSrsList() {
      const res = await API_SRS.list()
      this.srsList = res.data
    },
  },
}
</script>

<style scoped>
.v-btn {
  font-weight: 400;
}
</style>
